<template>
  <div class="bg-[#FFFFFF] rounded-xl px-4">
    <div class="space-y-1 font-inter text-xs+">
      <div class="flex items-center justify-between pt-3 pb-1">
        <div>
          <h1 class="text-[#0F172A] text-lg font-semibold">Calendar</h1>
        </div>
        <div class="-mr-1.5 flex space-x-2">
          <button
            @click="previousMonth"
            class="btn size-7 rounded-full p-0 hover:bg-slate-300/20 focus:bg-slate-300/20 active:bg-slate-300/25 dark:hover:bg-navy-300/20 dark:focus:bg-navy-300/20 dark:active:bg-navy-300/25"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="size-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            @click="nextMonth"
            class="btn size-7 rounded-full p-0 hover:bg-slate-300/20 focus:bg-slate-300/20 active:bg-slate-300/25 dark:hover:bg-navy-300/20 dark:focus:bg-navy-300/20 dark:active:bg-navy-300/25"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="size-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      <div class="flex items-center justify-between pb-4">
        <p class="font-medium text-slate-700 dark:text-navy-100">
          {{ monthyear }}
        </p>

        <div class="flex items-center justify-end gap-2">
          <select name="" id="year" class="p-1 rounded-lg" @change="changeYear">
            <option value="2025" :selected="year == 2025">2025</option>
            <option value="2026" :selected="year == 2026">2026</option>
            <option value="2027" :selected="year == 2027">2027</option>
            <option value="2028" :selected="year == 2028">2028</option>
          </select>

          <select
            name=""
            id="month"
            class="p-1 rounded-lg"
            @change="changeMonth"
          >
            <option
              v-for="(month, index) in months"
              :key="index"
              :value="index"
              :selected="index == this.month"
            >
              {{ month }}
            </option>
          </select>
        </div>
      </div>

      <!-- Legend -->
      <div
        class="flex flex-wrap items-center gap-4 pb-3 text-xs"
        v-if="userInfo?.user?.permission === 'student'"
      >
        <div class="flex items-center gap-1.5">
          <div class="w-2 h-2 bg-green-500 rounded-full"></div>
          <span>Accepted</span>
        </div>
        <div class="flex items-center gap-1.5">
          <div class="w-2 h-2 bg-red-500 rounded-full"></div>
          <span>Rejected</span>
        </div>
        <div class="flex items-center gap-1.5">
          <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
          <span>Meeting</span>
        </div>
        <div class="flex items-center gap-1.5">
          <div class="w-2 h-2 bg-orange-500 rounded-full"></div>
          <span>Overdue</span>
        </div>
        <div class="flex items-center gap-1.5">
          <div class="w-2 h-2 bg-purple-500 rounded-full"></div>
          <span>Submitted</span>
        </div>
      </div>

      <!-- Day headers -->
      <div class="grid grid-cols-7 pb-2">
        <div
          class="text-tiny+ font-semibold text-primary dark:text-accent-light"
        >
          SUN
        </div>
        <div
          class="text-tiny+ font-semibold text-primary dark:text-accent-light"
        >
          MON
        </div>
        <div
          class="text-tiny+ font-semibold text-primary dark:text-accent-light"
        >
          TUE
        </div>
        <div
          class="text-tiny+ font-semibold text-primary dark:text-accent-light"
        >
          WED
        </div>
        <div
          class="text-tiny+ font-semibold text-primary dark:text-accent-light"
        >
          THU
        </div>
        <div
          class="text-tiny+ font-semibold text-primary dark:text-accent-light"
        >
          FRI
        </div>
        <div
          class="text-tiny+ font-semibold text-primary dark:text-accent-light"
        >
          SAT
        </div>
      </div>

      <div class="grid grid-cols-7 place-items-center">
        <button
          v-for="day in prevDays"
          :key="'prev-' + day"
          class="relative flex h-12 w-14 items-center justify-center rounded-xl text-slate-400 hover:bg-primary/10 hover:text-primary dark:text-navy-300 dark:hover:bg-accent-light/10 dark:hover:text-accent-light"
        >
          {{ day }}
        </button>

        <button
          v-for="day in days"
          @click="getDateEmit(day)"
          :key="'curr-' + day"
          class="relative flex flex-col h-10 w-10 m-1 items-center justify-center rounded-xl dates text-slate-900 hover:bg-[#a3e6ef] hover:text-primary dark:text-navy-100 dark:hover:bg-accent-light/10 dark:hover:text-accent-light"
          :class="{
            'bg-blues text-white rounded-xl': isToday(day),
            'bg-yellow-500 text-white': isTodoContains(day),
            'text-[#4f46e5] font-semibold': isWeekend(day),
            'bg-black text-white': isNotificationOnDate(day),
            'bg-[#a3e6ef]': day === selectedDate,
          }"
        >
          <span class="text-sm">{{ day }}</span>

          <!-- Indicator dots with overlapping effect -->
          <div
            v-if="getDateIndicators(day).length > 0"
            class="flex items-center justify-center -space-x-1 mt-0.5"
          >
            <span
              v-for="(indicator, idx) in getDateIndicators(day)"
              :key="idx"
              :class="indicator.color"
              class="w-2 h-2 rounded-full border border-white"
              :style="{ zIndex: idx }"
            ></span>
          </div>
        </button>

        <button
          v-for="day in nextDays"
          :key="'next-' + day"
          class="relative flex h-7 w-9 items-center justify-center rounded-xl text-slate-400 hover:bg-primary/10 hover:text-primary dark:text-navy-300 dark:hover:bg-accent-light/10 dark:hover:text-accent-light"
        >
          {{ day }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      date: new Date(),
      month: new Date().getMonth(),
      year: new Date().getFullYear(),

      days: [],
      prevDays: [],
      nextDays: [],

      monthyear: "",
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      notifications: [],
      todolist: [],
      acceptedAssignments: [],
      rejectedAssignments: [],
      calendarItems: [],
      overdueAssignments: [],
      submittedAssignments: [],

      tooltipContent: "",
      tooltipDate: null,
      showTooltip: false,
      selectedDate: null,
      taskList: "",
    };
  },

  computed: mapGetters(["userInfo"]),

  created() {
    this.fetchNotifications();
    this.getTodo();
  },

  mounted() {
    this.getData();
    this.initializeCalendar();
    this.monthyear = `${this.months[this.month]} ${this.year}`;
  },

  methods: {
    getData() {
      axios
        .get(
          `${process.env.VUE_APP_API}/today-status/?user_id=${this.userInfo.user.id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          this.taskList = response.data.data;

          if (response.data.accepted_assignments) {
            this.acceptedAssignments = response.data.accepted_assignments;
          }
          if (response.data.rejected_assignments) {
            this.rejectedAssignments = response.data.rejected_assignments;
          }
          if (response.data.calendar_items) {
            this.calendarItems = response.data.calendar_items;
          }
          if (response.data.overdue_assignments) {
            this.overdueAssignments = response.data.overdue_assignments;
          }
          if (response.data.submitted_assignments) {
            this.submittedAssignments = response.data.submitted_assignments;
          }
        });
    },

    getDateIndicators(day) {
      const indicators = [];
      const currentDate = new Date(this.year, this.month, day);
      if (
        this.hasItemOnDate(
          currentDate,
          this.acceptedAssignments,
          "verification_date",
        )
      ) {
        indicators.push({ type: "accepted", color: "bg-green-500" });
      }
      if (
        this.hasItemOnDate(
          currentDate,
          this.rejectedAssignments,
          "verification_date",
        )
      ) {
        indicators.push({ type: "rejected", color: "bg-red-500" });
      }
      if (this.hasItemOnDate(currentDate, this.calendarItems, "start_date")) {
        indicators.push({ type: "meeting", color: "bg-blue-500" });
      }
      if (
        this.hasItemOnDate(currentDate, this.overdueAssignments, "submit_date")
      ) {
        indicators.push({ type: "overdue", color: "bg-orange-500" });
      }
       if (
        this.hasItemOnDate(currentDate, this.submittedAssignments, "submit_date")
      ) {
        indicators.push({ type: "submitted", color: "bg-purple-500" });
      }

      return indicators;
    },

    hasItemOnDate(currentDate, items, dateField) {
      if (!items || items.length === 0) {
        return false;
      }

      for (let i = 0; i < items.length; i++) {
        let itemDate = items[i][dateField];
        if (!itemDate) continue;

        let parsedDate = new Date(itemDate)
          .toISOString()
          .split("T")[0]
          .split("-");

        let convertedDate = new Date(
          parseInt(parsedDate[0]),
          parseInt(parsedDate[1]) - 1,
          parseInt(parsedDate[2]),
        );

        if (currentDate.getTime() === convertedDate.getTime()) {
          return true;
        }
      }
      return false;
    },

    getDateEmit(day) {
      this.selectedDate = day;
      this.$emit(
        "addFilterEmit",
        `${this.year}-${parseInt(this.month) + 1}-${day}`,
      );
    },

    yearFilter(year) {
      axios
        .get(
          `${process.env.VUE_APP_API}/notifications/${
            this.userInfo.user.id
          }/?Key=${this.userInfo.token_key}&month=${
            parseInt(this.month) + 1
          }&year=${parseInt(year)}`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.notifications = response.data.data.data;
          }
        })
        .catch((error) => {
          console.error("Error fetching attendance data:", error);
        });

      axios
        .get(
          `${process.env.VUE_APP_API}/to-do-get/${this.userInfo.user.id}/?Key=${
            this.userInfo.token_key
          }&month=${parseInt(this.month) + 1}&year=${parseInt(
            year,
          )}&search=false`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.todolist = response.data.data.data;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    monthFilter(month) {
      axios
        .get(
          `${process.env.VUE_APP_API}/notifications/${
            this.userInfo.user.id
          }/?Key=${this.userInfo.token_key}&month=${parseInt(month) + 1}&year=${
            this.year
          }`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.notifications = response.data.data.data;
          }
        })
        .catch((error) => {
          console.error("Error fetching attendance data:", error);
        });

      axios
        .get(
          `${process.env.VUE_APP_API}/to-do-get/${this.userInfo.user.id}/?Key=${
            this.userInfo.token_key
          }&month=${parseInt(month) + 1}&year=${this.year}&search=false`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.todolist = response.data.data.data;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async getTodo() {
      await axios
        .get(
          `${process.env.VUE_APP_API}/to-do-get/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}&search=false`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.todolist = response.data.data.data;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    isTodoContains(day) {
      let currentDate = new Date(this.year, this.month, day);
      for (let i = 0; i < this.todolist.length; i++) {
        let todoDate = this.todolist[i].created_at;
        let todoCDate = new Date(todoDate)
          .toISOString()
          .split("T")[0]
          .split("-");
        let convertNotDate = new Date(
          parseInt(todoCDate[0]),
          parseInt(todoCDate[1]) - 1,
          parseInt(todoCDate[2]),
        );
        if (currentDate.getTime() === convertNotDate.getTime()) {
          return true;
        }
      }
      return false;
    },

    fetchNotifications() {
      axios
        .get(
          `${process.env.VUE_APP_API}/notifications/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.notifications = response.data.data.data;
          }
        })
        .catch((error) => {
          console.error("Error fetching attendance data:", error);
        });
    },

    isNotificationOnDate(day) {
      let currentDate = new Date(this.year, this.month, day);

      for (let i = 0; i < this.notifications.length; i++) {
        let notification = this.notifications[i].created_at;
        let notificationDate = new Date(notification)
          .toISOString()
          .split("T")[0]
          .split("-");

        let convertNotDate = new Date(
          parseInt(notificationDate[0]),
          parseInt(notificationDate[1]) - 1,
          parseInt(notificationDate[2]),
        );

        if (currentDate.getTime() === convertNotDate.getTime()) {
          return true;
        }
      }
      return false;
    },

    NotificationOnDateOfData(day) {
      let currentDate = new Date(this.year, this.month, day);

      for (let i = 0; i < this.notifications.length; i++) {
        let notification = this.notifications[i].created_at;
        let notificationDate = new Date(notification)
          .toISOString()
          .split("T")[0]
          .split("-");

        let convertNotDate = new Date(
          parseInt(notificationDate[0]),
          parseInt(notificationDate[1]) - 1,
          parseInt(notificationDate[2]),
        );

        if (currentDate.getTime() === convertNotDate.getTime()) {
          return this.notifications[i].topic;
        }
      }
      return "";
    },

    changeYear() {
      this.year = document.getElementById("year").value;
      this.getMonthDays();
      this.updateCalendar();
      this.yearFilter(this.year);
    },

    changeMonth() {
      this.month = document.getElementById("month").value;
      this.getMonthDays();
      this.updateCalendar();
      this.monthFilter(this.month);
    },

    getMonthDays() {
      this.days = [];
      const lastDate = new Date(
        parseInt(this.year),
        parseInt(this.month) + 1,
        0,
      ).getDate();
      for (let i = 1; i <= lastDate; i++) {
        this.days.push(i);
      }
    },

    getPrevDates() {
      this.prevDays = [];
      const firstDayOfWeek = new Date(
        parseInt(this.year),
        parseInt(this.month),
        1,
      ).getDay();
      const lastDateOfPrevMonth = new Date(
        parseInt(this.year),
        parseInt(this.month),
        0,
      ).getDate();
      for (let i = firstDayOfWeek; i > 0; i--) {
        this.prevDays.push(lastDateOfPrevMonth - i + 1);
      }
    },

    getNextDays() {
      this.nextDays = [];
      const lastDate = new Date(
        parseInt(this.year),
        parseInt(this.month) + 1,
        0,
      ).getDate();
      const lastDayOfWeek = new Date(
        parseInt(this.year),
        parseInt(this.month),
        lastDate,
      ).getDay();

      // If month ends on Saturday (6), no next days needed
      if (lastDayOfWeek === 6) return;

      for (let i = 1; i <= 6 - lastDayOfWeek; i++) {
        this.nextDays.push(i);
      }
    },

    async initializeCalendar() {
      this.updateCalendar();
    },

    isToday(i) {
      return (
        i === new Date().getDate() &&
        parseInt(this.month) === new Date().getMonth() &&
        parseInt(this.year) === new Date().getFullYear()
      );
    },

    isWeekend(i) {
      let dayOfWeek = new Date(this.year, this.month, i).getDay();
      return dayOfWeek === 6 || dayOfWeek === 0;
    },

    previousMonth() {
      this.month--;
      if (this.month < 0) {
        this.month = 11;
        this.year--;
      }
      this.updateCalendar();
      this.monthFilter(this.month);
    },

    nextMonth() {
      this.month++;
      if (this.month > 11) {
        this.month = 0;
        this.year++;
      }
      this.updateCalendar();
      this.monthFilter(this.month);
    },

    updateCalendar() {
      // this.days = [];
      // this.prevDays = [];
      // this.nextDays = [];
      this.getMonthDays();
      this.getPrevDates();
      this.getNextDays();
      this.monthyear = `${this.months[this.month]} ${this.year}`;
    },

    handleMouseOver(day) {
      if (this.isNotificationOnDate(day)) {
        this.tooltipContent = this.NotificationOnDateOfData(day);
        this.tooltipDate = day;
        this.showTooltip = true;
      }
    },
  },
};
</script>
